<template>
  <div class="select-content card-body p-2">
    <slot name="alert" />

    <b-row class="select-content__form">
      <b-col md="6">
        <FormulateInput
          name="transfer_reason"
          type="select-resourceful-v2"
          :label="$t('fleetTask.transferDeliveryModal.select.transferReason')"
          :placeholder="$t('fleetTask.transferDeliveryModal.select.selectTransferReason')"
          validation="required"
          :help="true"
          :isMandatory="true"
          :value="transferReasonFullObjectProxy?.id"
          :optionsGetter="transferReasonApiClient.get"
          :optionLabelFn="({ item }) => item.reason"
          :fullObject.sync="transferReasonFullObjectProxy"
        />
      </b-col>

      <b-col
        v-if="hasSingleProductCategoryAccess && productCategoryItem?.[0].value !== null"
        md="6"
        class="align-content-center"
      >
        <ReadOnly :items="productCategoryItem" />
      </b-col>

      <b-col v-else md="6">
        <radio-button-wrapper
          v-model="transferPurposeTypeProxy"
          name="transfer_purpose_type"
          :label="$t('fleetTask.transferDeliveryModal.select.selectTransferDestination')"
          :options="transferPurposeOptions"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-if="hasSingleProductCategoryAccess && productCategoryItem?.[0].value !== null"
        md="12"
      >
        <radio-button-wrapper
          v-model="transferPurposeTypeProxy"
          name="transfer_purpose_type"
          :label="$t('fleetTask.transferDeliveryModal.select.selectTransferDestination')"
          :options="transferPurposeOptions"
        />
      </b-col>
    </b-row>

    <div v-show="transferPurposeTypeProxy === 'exist'">
      <hr />

      <div class="select-content__exist-fleet-task">
        <div class="select-content__title">
          <label>{{ $t('fleetTask.transferDeliveryModal.select.selectFleetTask') }}</label>
        </div>

        <div class="select-content__table-searchable">
          <div class="select-content__header-container">
            <b-input-group size="sm" class="mc-has-search w-25" @click="focusSearch">
              <template #prepend>
                <i class="mc-icon ps-2 icon-fill_search"></i>
              </template>

              <b-form-input
                ref="searchInput"
                :value="search"
                :disabled="isBusy"
                :placeholder="$t('global.search') + '...'"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button class="clear-search-btn" :disabled="isBusy" @click="handleClearSearch">
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </div>

          <div class="select-content__table-container">
            <b-table
              ref="table-ref"
              class="-w-auto text-nowrap sticky-head-table border"
              select-mode="single"
              show-empty
              selectable
              :fields="fields"
              :items="items"
              @row-clicked="handleRowClicked"
            >
              <!-- Slot: Empty -->
              <template #empty>
                <div v-show="!isBusy" class="text-center my-4">
                  {{ $t('fleetTask.transferDeliveryModal.select.noData') }}
                </div>
              </template>

              <!-- Table Header -->
              <template #head()="data">
                <div @click="handleSortChange(data)">
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                      <i class="mc-icon icon-outline_chevron-up"></i>
                    </div>
                    <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                      <i class="mc-icon icon-outline_chevron-down"></i>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Status -->
              <template #cell(status.name)="{ value, item }">
                <span
                  v-if="item.status?.color"
                  class="__status-dots"
                  :style="{ backgroundColor: item.status.color }"
                ></span>
                <span class="mc-text-status">{{ value }}</span>
              </template>

              <template v-slot:[`cell(vehicle.license_plate)`]="{ item }">
                {{ item?.vehicle?.license_plate ?? '-' }}
                <div
                  class="text-muted"
                  v-if="item?.vehicle?.hull_no"
                >
                  {{ item?.vehicle?.hull_no }}
                </div>
              </template>
            </b-table>
          </div>

          <div class="select-content__footer-container">
            <div class="__total-data">
              <span>
                {{ $t('fleetTask.transferDeliveryModal.select.showing') }}
                <strong>{{ rangeCount }}</strong>
                {{ $t('fleetTask.transferDeliveryModal.select.of') }}
                <strong>{{ totalCount }}</strong>
              </span>
            </div>

            <div class="__pagination">
              <b-pagination
                :disabled="isBusy"
                :per-page="limit"
                :total-rows="totalCount"
                :value="page"
                @change="handlePageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'

import { mapState, mapGetters } from 'vuex'
import { debounce } from '@/helpers/debounce'

import RadioButtonWrapper from '../components/RadioButtonWrapper.vue'
import ReadOnly from '../../ReadOnly.vue'

import * as FleetTask from '@/services/rest-api/lib/fleet-task/fleet-task'
import * as Reason from '@/services/rest-api/lib/delivery/reason'

export default {
  components: {
    RadioButtonWrapper,
    ReadOnly,
  },
  mixins: [Helper],
  props: {
    selectedFOId: { type: String },
    transferReasonFullObject: {
      type: Object,
      required: true,
    },
    transferPurposeType: {
      type: String,
      required: true,
    },
  },
  emits: ['update:newFleetTask', 'update:transferReasonFullObject', 'update:transferPurposeType'],
  data() {
    return {
      isBusy: false,

      totalCount: null,
      page: 1,
      limit: 5,
      search: '',

      sorts: [],
      items: [],
      ids: [],

    }
  },
  computed: {
    ...mapState('fleetTask', ['fleetTaskDetail']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read'])
    },
    transferPurposeOptions() {
      return [
        {
          key: 'exist',
          icon: 'icon-outline_fleet-order',
          label: this.$t('fleetTask.transferDeliveryModal.initial.options.exist.title'),
        },
        {
          key: 'create',
          icon: 'icon-outline_list-add',
          label: this.$t('fleetTask.transferDeliveryModal.initial.options.create.title'),
        },
      ]
    },
    fields() {
      return [
        {
          label: this.$t('fleetTask.transferDeliveryModal.fields.fleetTaskNumber'),
          key: 'number',
          isSortable: true,
        },
        {
          label: this.$t('fleetTask.transferDeliveryModal.fields.status'),
          key: 'status.name',
          isSortable: true,
        },
        {
          label: this.$t('fleetTask.fields.licensePlate'),
          key: 'vehicle.license_plate',
          isSortable: true,
        },
        {
          label: this.$t('fleetTask.fields.vehicleType'),
          key: 'vehicle.type_name',
          isSortable: true,
        },
        {
          label: this.$t('fleetTask.fields.driver'),
          key: 'driver_user.name',
          isSortable: true,
        },
      ]
    },
    rangeCount() {
      let lastNumber = this.limit * this.page
      const firstNumber = lastNumber - (this.limit - 1)

      if (lastNumber > this.totalCount) {
        lastNumber = this.totalCount
      }

      return `${firstNumber} - ${lastNumber}`
    },
    transferReasonApiClient() {
      return {
        get: Reason.get(`type eq 'TRANSFERRED'`),
      }
    },
    transferReasonFullObjectProxy: {
      get() {
        return this.transferReasonFullObject
      },
      set(val) {
        this.$emit('update:transferReasonFullObject', val)
      },
    },
    transferPurposeTypeProxy: {
      get() {
        return this.transferPurposeType
      },
      set(val) {
        this.$emit('update:transferPurposeType', val)
      },
    },
    hasSingleProductCategoryAccess() {
      return this.hasAccessAll(['fleet-task-single-category-deliveries:read'])
    },
    productCategoryItem() {
      return [
        {
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.productCategory'),
          value: this.fleetTaskDetail?.product_category?.name ?? null,
        },
      ]
    },
  },
  async mounted() {
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    async getData(opts) {
      let filters = [
        'status.name in (DRAFT SCHEDULED STARTED)',
        `id ne ${this.fleetTaskDetail.id}`,
        `vendor.id is null`,
        `is_from_vendor_request eq false`,
      ]

      if (this.hasTmsUserGroupAccess) {
        filters = [
          ...filters,
          ...(this.isSuperUser() ? [`user_group.id eq ${this.fleetTaskDetail.user_group.id}`] : []),
        ]
      }

      if (this.hasSingleProductCategoryAccess) {
        filters = [
          ...filters,
          ...(this.fleetTaskDetail?.product_category?.id
            ? [`product_category.id eq ${this.fleetTaskDetail.product_category.id}`]
            : [`product_category.id is null`]),
        ]
      }

      this.isBusy = true
      const getFleetTask = FleetTask.get(filters)
      const res = await getFleetTask(opts)

      if (!res || res?.errors) return

      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count

      this.isBusy = false
      await this.$nextTick()

      this.highlightSelectedRow()
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // highlight the row if there is selected value from parent
    highlightSelectedRow() {
      if (this.selectedFOId == null) return

      // intentionally used == instead of === because selectedModel can be a string or number (id or uuid)
      const index = this.items.findIndex((item) => item.id == this.selectedFOId)

      // Toggle the row checkbox
      if (index !== -1) {
        this.$refs['table-ref'].selectRow(index)
      }
    },
    handleRowClicked(item) {
      if (this.selectedFOId == item.id) {
        this.$emit('update:newFleetTask', {})
        return
      }

      this.$emit('update:newFleetTask', item)
    },
  },
}
</script>

<style lang="scss" scoped>
.select-content {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__exist-fleet-task {
    display: flex;
    flex-direction: column;
  }

  &__table-searchable {
    border: 2px solid #ebecef;
  }

  &__header-container {
    display: flex;
    justify-content: end;
    padding: 12px;

    .mc-has-search {
      border-width: $input-border-width;
      border-color: $input-border-color;
      border-radius: 4px;
      border-style: solid;
      margin-left: 0;

      border-radius: 4px;

      // if search is disabled
      &:has(input:disabled) {
        background: $input-disabled-bg;
      }

      .clear-search-btn {
        border: none;
        background-color: transparent;
        padding: 0px;
        margin: 0px;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .clear-search-btn:disabled {
        cursor: default;
      }

      input {
        border: none;
        height: 37px;
      }

      .input-group-prepend,
      .input-group-append {
        border: none;
        display: flex;
        align-items: center;

        .mc-icon:before {
          font-size: 16px;
          color: $input-placeholder-color;
        }
      }
    }
  }

  &__table-container {
    border: 1px solid #ebecef;
    border-bottom: none;
    height: 300px;
    overflow-y: auto;

    :deep(.table) {
      --bs-table-active-bg: #e9f2fc;
      border-left: none !important;
      border-right: none !important;

      .__status-dots {
        display: inline-block;
        margin: auto;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        margin-right: 8px;
      }

      thead {
        tr {
          th {
            position: relative;
            &:has(.sort-icon-up),
            &:has(.sort-icon-down) {
              padding-right: 30px;
            }

            .sort-icon-up {
              position: absolute;
              right: 10px;
              top: 8px;
              opacity: 0.7;
            }
            .sort-icon-down {
              position: absolute;
              right: 10px;
              bottom: 8px;
              opacity: 0.7;
            }
          }
        }
      }
    }
  }

  &__footer-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 12px;

    .__total-data {
      color: $mep-color-text-400;
      margin: 0;

      strong {
        font-weight: 600;
      }
    }

    .__pagination {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      // remove default margin from pagination
      ul[role='menubar'] {
        margin-bottom: 0;
      }

      @media screen and (max-width: 468px) {
        justify-content: center;
      }

      :deep(.pagination) {
        --bs-pagination-border-width: 0;
        --bs-pagination-border-radius: 100%;

        .page-link {
          padding: 4px;
          min-height: 24px;
          aspect-ratio: 1 / 1;
          border-radius: 100%;
          font-size: 12px;
        }

        .page-link.active,
        .active > .page-link {
          background-color: $mep-primary-400;
        }

        .page-item {
          &:nth-child(1),
          &:nth-child(2),
          &:nth-last-child(1),
          &:nth-last-child(2) {
            .page-link {
              display: flex;
              justify-content: center;
              align-items: center;
              font-size: 16px;
              padding-bottom: 10%;
            }
          }
        }
      }
    }
  }
}
</style>
